import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, finalize, map } from 'rxjs';

import {
  User,
  LoginEmailRequest,
  LoginResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  ResetEmailRequest,
  ChangePasswordRequest,
  ActivateAccountRequest,
  RegisterUser,
  EnableTwoFactorRequest,
  TwoFactorType,
} from '@shared/models';

import { BaseService } from '../base.service';
import { httpParams } from '../http-params.builder';
import { LoadersService } from '../loaders.service';

@Injectable({
  providedIn: 'root',
})
export class AuthHttpService extends BaseService {
  constructor(
    private http: HttpClient,
    private loadersService: LoadersService
  ) {
    super();
  }

  getCurrentUser(loader: string): Observable<User> {
    this.loadersService.show(loader);
    return this.http.get<User>(this.baseUrl + '/auth').pipe(finalize(() => this.loadersService.hide(loader)));
  }

  login(loader: string, request: LoginEmailRequest): Observable<LoginResponse | null> {
    this.loadersService.show(loader);
    return this.http
      .post<LoginResponse>(this.baseUrl + '/auth/login/email', request)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  refreshToken(loader: string, refreshToken: string): Observable<LoginResponse | null> {
    this.loadersService.show(loader);
    return this.http
      .post<LoginResponse>(this.baseUrl + '/auth/refresh-token', { refreshToken: refreshToken })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  forgotPassword(loader: string, dto: ForgotPasswordRequest): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.post(this.baseUrl + '/auth/forgot-password', dto, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  resetPassword(loader: string, dto: ResetPasswordRequest): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.post(this.baseUrl + '/auth/reset-password', dto, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  resetEmail(loader: string, dto: ResetEmailRequest): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.post(this.baseUrl + '/auth/reset-email', dto, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  changePassword(loader: string, dto: ChangePasswordRequest): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.post(this.baseUrl + '/auth/change-password', dto, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  activateAccount(loader: string, dto: ActivateAccountRequest): Observable<LoginResponse> {
    this.loadersService.show(loader);
    return this.http
      .post<LoginResponse>(this.baseUrl + '/auth/activate-account', dto)
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  register(loader: string, dto: RegisterUser): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.post(this.baseUrl + '/auth/register/email/business-owner', dto, { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }

  switchUserLoginCompany(loader: string, companyId: string): Observable<LoginResponse | null> {
    this.loadersService.show(loader);
    return this.http
      .post<LoginResponse>(this.baseUrl + '/auth/change-company', { companyId: companyId })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  confirmRegister(loader: string, email: string, token: string): Observable<LoginResponse | null> {
    this.loadersService.show(loader);
    return this.http
      .get<LoginResponse>(this.baseUrl + '/auth/confirm-register', {
        params: httpParams().from({ email, token }).build(),
      })
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  enableTwoFactor(loader: string, type: TwoFactorType): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http
      .post(this.baseUrl + '/auth/two-factor', new EnableTwoFactorRequest(type), { observe: 'response' })
      .pipe(
        map((response) => this.isStatusSucceed(response.status)),
        finalize(() => this.loadersService.hide(loader))
      );
  }

  generateMobileAuthToken(loader: string): Observable<LoginResponse> {
    this.loadersService.show(loader);
    return this.http
      .post<LoginResponse>(this.baseUrl + '/auth/mobile-auth-token', {})
      .pipe(finalize(() => this.loadersService.hide(loader)));
  }

  deleteMyAccount(loader: string): Observable<boolean> {
    this.loadersService.show(loader);
    return this.http.delete(this.baseUrl + '/users/my', { observe: 'response' }).pipe(
      map((response) => this.isStatusSucceed(response.status)),
      finalize(() => this.loadersService.hide(loader))
    );
  }
}
